/* eslint-disable import/prefer-default-export */

import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import {
  DEV_HOSTNAME,
  MEX_PORTAL_PRD_HOSTNAME,
  MEX_PORTAL_STG_HOSTNAME,
  PRD_HOSTNAME,
  STG_HOSTNAME,
  MEX_PORTAL_DEV_HOSTNAME,
  CHOPE_CRM_PATH,
} from 'src/constants/appConfigs';

dayjs.extend(duration);

export const isNotRunningTest = (): boolean => process.env.JEST_WORKER_ID === undefined;

export const isProdEnv = (): boolean =>
  window?.location?.hostname === PRD_HOSTNAME || window?.location?.hostname === MEX_PORTAL_PRD_HOSTNAME;

export const isStagingEnv = (): boolean =>
  window?.location?.hostname === STG_HOSTNAME || window?.location?.hostname === MEX_PORTAL_STG_HOSTNAME;

export const isDevEnv = (): boolean =>
  window?.location?.hostname === DEV_HOSTNAME || window?.location?.hostname === MEX_PORTAL_DEV_HOSTNAME;

export const isBranchEnv = (): boolean => window?.location?.hostname.endsWith(`-${STG_HOSTNAME}`);

export const isUsingStagingEnv = (): boolean => isStagingEnv() || isDevEnv() || isBranchEnv();

export const getEnv = (): string => {
  if (isProdEnv()) return 'prd';
  if (isStagingEnv()) return 'stg';
  if (isDevEnv()) return 'dev';
  if (isBranchEnv()) return 'stg';
  return '';
};
export const getApiEnv = (): string => (isProdEnv() ? 'prd' : 'stg');

export const getLoginUrl = (): string => {
  const hostname = isProdEnv() ? MEX_PORTAL_PRD_HOSTNAME : MEX_PORTAL_STG_HOSTNAME;
  const base = `https://${hostname}/portal`;
  const path = typeof window !== 'undefined' ? window.location.pathname : '';
  const search = typeof window !== 'undefined' ? window.location.search : '';
  if (path.includes(CHOPE_CRM_PATH)) {
    const redir = encodeURIComponent(path + search);
    return `${base}?redir=${redir}`;
  }
  return base;
};

export const getFullEnv = (env: string): string => {
  switch (env) {
    case 'stg':
      return 'staging';
    case 'prd':
      return 'production';
    case 'misc':
      return 'miscellaneous';
    default:
      return '';
  }
};

export const getStringBetween = (str: string, start: string, end: string): string => {
  const result = str.match(new RegExp(`(?<=${start})(.*)(?=${end})`));
  return result?.[1] || '';
};

export const formatLongText = (src: string, maxLength: number): string => {
  if (src.length > maxLength) {
    return `${src.substring(0, maxLength)}...`;
  }
  return src;
};

export const formatJobDuration = (milliseconds: number, customFormat?: string): string => {
  if (milliseconds < 0) {
    return '-';
  }
  if (milliseconds < 1000) {
    return `${milliseconds}ms`;
  }
  return dayjs.duration(milliseconds).format(customFormat || 'H[h] m[m] s[s]');
};

export const camelCase = (str: string): string =>
  str
    .replace(/[-_]([a-z])/g, (g) => g[1].toUpperCase())
    .replace(/(?:^\w|[A-Z]|\b\w)/g, (word, index) => (index === 0 ? word.toLowerCase() : word.toUpperCase()))
    .replace(/\s+/g, '');

export const formatTimestamp = (
  date: string | number | dayjs.Dayjs | Date | null | undefined,
  format = 'YYYY-MM-DD HH:mm:ss',
): string => {
  if (!date) {
    return '-';
  }
  if (typeof date === 'number' && date < 0) {
    return '-';
  }
  return dayjs(date).format(format);
};

export const capitalizeFirstLetter = (s: string | undefined): string => (s ? s[0].toUpperCase() + s.slice(1) : '');

export const accFromEmail = (email: string): string => email?.replace('@grabtaxi.com', '') || '';

export const urlify = (text = ''): string => {
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  return text.replace(urlRegex, (url) => `<a href="${url}">${url}</a>`);
};

export const queryStringify = (obj: any): string => `?${new URLSearchParams(obj).toString()}`;

export { init as initI18n } from './i18n';

export const camelCaseToTitleCase = (camelCaseString: string): string => {
  // Add a space before any uppercase letter and capitalize only the first letter
  const result = camelCaseString
    .replace(/([A-Z])/g, ' $1') // Add space before uppercase letters
    .toLowerCase() // Convert everything to lowercase first
    .replace(/^./, (str) => str.toUpperCase()); // Capitalize only the first letter
  return result.trim(); // Remove any leading or trailing spaces
};

export function GET_USERPROFILE_FROM_LS() {
  try {
    return JSON.parse(localStorage.getItem('userprofileInfo') ?? '');
  } catch {
    return null;
  }
}

export const isWebloginFeatureEnabled = () => {
  try {
    // This is due to MEX portal can be opened through a WebView that would need to be authenticated
    // so we can't use weblogin in this case and need to fallback to the old flow instead. When using
    // cookie authn, the JWT will be empty so ideally under normal circumstances whenever the JWT is
    // injected to the WebView, we should be able to fallback to use the old flow while the normal
    // weblogin flow shouldn't be affected.
    const jwtToken = localStorage.getItem('JWT');
    // As of now, this fn is being called in 3 places, help center check, isLoggedIn check and in
    // changeLanguage fn. In help center check and changeLanguage fn, they're "guarded" by the isLoggedIn
    // check. In isLoggedIn check, under normal circumstances, the JWT should be empty for cookie authn
    // case, while in injected session WebView case, it'll be present so we can falback to the old flow.
    // We don't need the unauthenticated path check here since all these 3 functions are more or less being
    // called in sidebar component, which would only be shown in authenticated state.
    return !jwtToken;
  } catch (e) {
    return false;
  }
};

// check if it's logged in using weblogin or JWT
export const isLoggedInPlatform = function () {
  const isWebloginEnabled = isWebloginFeatureEnabled();
  const userProfile = GET_USERPROFILE_FROM_LS();
  return isWebloginEnabled ? !!userProfile : localStorage.getItem('JWT') != null;
};
